import clsx from "clsx";
import {
    DeepRequired,
    FieldErrors,
    FieldErrorsImpl,
    FieldName,
    FieldValues,
    Path,
    UnPackAsyncDefaultValues,
    UseFormRegister,
} from "react-hook-form";
import { FieldValuesFromFieldErrors } from "@hookform/error-message";
import { ErrorMessageOrHelperText } from "components/fields/items/ErrorMessageOrHelperText";
import { ReactElement } from "react";
import { AdditionalInfoButton } from "components/AdditionalInfoButton";

export function SelectList<T extends FieldValues>(props: {
    register: UseFormRegister<T>;
    name: Path<UnPackAsyncDefaultValues<T>> &
        FieldName<FieldValuesFromFieldErrors<Partial<FieldErrorsImpl<DeepRequired<UnPackAsyncDefaultValues<T>>>>>>;
    label?: string;
    required?: boolean;
    disabled?: boolean;
    hideLabel?: boolean;
    errors: FieldErrors<T>;
    helperText?: string;
    additionalInfo?: ReactElement;
    inline?: boolean;
    id?: string;
    className?: string;
    options: Array<{ value: string; label: string }>;
    defaultOptionLabel?: string;
}) {
    const {
        name,
        register,
        disabled,
        required,
        hideLabel,
        label = name,
        errors,
        helperText,
        inline,
        additionalInfo,
        id,
        className,
        options,
        defaultOptionLabel,
    } = props;

    const inputId = id ?? name;

    return (
        <div
            className={clsx(
                "d-flex gap-2",
                inline ? "align-items-lg-center w-100 flex-column flex-lg-row" : "flex-column",
            )}
        >
            <div className="d-flex">
                <label
                    htmlFor={inputId}
                    className={clsx(
                        hideLabel ? "visually-hidden" : "d-flex form-label align-items-between gap-2 flex-row fw-bold",
                        required && "required",
                        inline && "text-nowrap",
                    )}
                >
                    {label}
                </label>

                {additionalInfo && <AdditionalInfoButton additionalInfo={additionalInfo} />}
            </div>

            <select
                id={inputId}
                className={clsx("form-select", className)}
                disabled={disabled}
                {...register(name, { required })}
            >
                {defaultOptionLabel && <option value="">{defaultOptionLabel}</option>}
                {options.map(option => (
                    <option key={option.value} value={option.value}>
                        {option.label}
                    </option>
                ))}
            </select>

            <ErrorMessageOrHelperText errors={errors} name={name} helperText={helperText} />
        </div>
    );
}
